import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActionsBar from '../shared/DialogActionsBar';
import {
  useVersionMismatchStore,
  resetVersionMismatchFlag,
} from '../../stores/versionMismatchStore';

/**
 * Blocking modal shown when a request routed to a pinned API version slot
 * comes back 404 — the slot's build is missing the route. The React app and
 * its API slot are meant to be deployed together, so this is a deployment
 * mismatch, not a feature the user wandered into. We alarm loudly rather than
 * silently falling back to the main API (which would hand a pinned customer a
 * feature their version isn't meant to have).
 *
 * It is blocking (modal, cannot be dismissed by clicking away or Escape) but
 * offers an explicit Reload/Dismiss so a stray 404 can't permanently trap the
 * user.
 */
export default function VersionMismatchDialog() {
  const mismatch = useVersionMismatchStore((s) => s.mismatch);

  if (!mismatch) return null;

  const handleReload = () => {
    resetVersionMismatchFlag();
    window.location.reload();
  };

  const handleDismiss = () => {
    resetVersionMismatchFlag();
  };

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}
      onClose={(_e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <i
          className="bi bi-exclamation-triangle"
          style={{ fontSize: 'var(--font-size-large)', color: 'var(--color-negative)' }}
        />
        <span>Feature Unavailable</span>
      </DialogTitle>

      <DialogContent>
        {/*
          This alarm fires on ANY 404 from a version-pinned API host, and it
          cannot tell "missing in THIS version" from "never existed anywhere".

          It stated the first as fact. A route that was never implemented in
          any version — /api/FoodAllergy/Warnings, unbuilt from March to August
          2026 — told facilities they were pinned to a version lacking the
          feature and to contact support to align it. No version had it, so
          support could not have fixed anything.

          The wording now describes what is actually known: a request came back
          404 from the pinned host. The version is still shown because it is
          genuinely useful to whoever investigates, but it is no longer named
          as the cause.
        */}
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
          This feature could not be loaded. The request below was not found on
          the API your facility is currently using
          (version <strong>{mismatch.webVersion}</strong>).
        </p>
        <p style={{ fontSize: 'var(--font-size-small)', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
          That is usually a version mismatch, but it can also mean the feature
          is not built yet. Please send the details below to PSI support rather
          than requesting a version change. They identify which it is.
        </p>
        <div
          style={{
            fontSize: 'var(--font-size-small)',
            color: 'var(--text-muted)',
            background: 'var(--surface-2, rgba(0,0,0,0.04))',
            borderRadius: 4,
            padding: '8px 10px',
            wordBreak: 'break-all',
          }}
        >
          <div>Version: {mismatch.webVersion}</div>
          <div>API host: {mismatch.host}</div>
          <div>Route: {mismatch.route}</div>
        </div>
      </DialogContent>

      <DialogActionsBar
        onCancel={handleDismiss}
        cancelLabel="Dismiss"
        onSave={handleReload}
        saveLabel="Reload"
      />
    </Dialog>
  );
}
